import { IResultMessage } from "@multiweb/types/contracts/responses";
import { convertResultMessages } from "OW.Remoting/Remoting/Clients/impl/utils/response";
import { IHTTPResult } from "../../Global/Base/Comunication/HTTPRequest";
import { CaseInsensitiveMap } from "../../Global/Base/DataTypes/CaseInsensitiveMap";
import { InvalidArgumentException } from "../../Global/Base/Exceptions";
import { contextlessInstanceOf } from "../../Global/Base/Polyfill/instanceOf";
import { IValidationError, JSONSRVException, ValidationErrorType } from "./EOWExceptionsInterfaces";

/** Informace o HTTP requestu, který se váže k chybě */
export interface IHttpExceptionRequest {
    /** Metoda requestu - GET, POST, atp. */
    readonly requestMethod: string;
    /** Url requestu */
    readonly url: string;
    /** HTTP hlavičky requestu */
    readonly headers?: Iterable<[string, string]> | { [name: string]: string | boolean };
    /** Tělo (obsah) requestu */
    readonly data?: any;
}

export class EOWException extends Error {
    // property message nejde standardne overridnout
    public readonly logExceptionID: string | undefined;
    public readonly serverStackTrace: string | undefined;
    public httpErrorCode: number | undefined;
    public request: IHttpExceptionRequest | undefined;

    constructor(faultElement?: string | JSONSRVException | EOWException) {
        let message: string = "";
        let serverStackTrace: string | undefined;
        let logExceptionID: string | undefined;
        if (typeof faultElement === "string")
            message = faultElement;
        else if (faultElement instanceof EOWException) { // copy constructor
            message = faultElement.message;
            serverStackTrace = faultElement.serverStackTrace;
            logExceptionID = faultElement.logExceptionID;
        } else if (faultElement) {
            logExceptionID = faultElement.LogExceptionID;
            serverStackTrace = faultElement.StackTrace;
            if (faultElement.ExceptionDetails && faultElement.ExceptionDetails.Text)
                message = faultElement.ExceptionDetails.Text;
            else
                message = faultElement.Message;
        }

        super(message);
        if (logExceptionID)
            this.logExceptionID = logExceptionID;
        if (serverStackTrace)
            this.serverStackTrace = serverStackTrace;
    }

    public static override [Symbol.hasInstance](instance: any): boolean {
        return instance != null && contextlessInstanceOf(instance, OWExceptions.getRegisteredExceptionType(this.name));
    }

    public override toString(): string {
        return this.message;
    }

    public static getParams(faultText: string): { msg: string; params?: string[] } {
        const pos = faultText.indexOf(" \u00a0(");
        if (pos > -1) {
            const msg = faultText.substring(0, pos);
            const params = faultText.substring(pos + 3, faultText.length - 1).split(", ");
            return { msg: msg, params: params };
        }
        return { msg: faultText };
    }
}

export class EOWLogonError extends EOWException {
    public readonly userName: string | undefined;
    public readonly code: string | undefined;

    constructor(faultElement: string | JSONSRVException) {
        if (typeof faultElement === "string") {
            const tmp = EOWException.getParams(faultElement);
            super(tmp.msg);
            if (tmp.params) {
                this.userName = tmp.params[0];
                this.code = tmp.params[1];
            }
        } else {
            super(faultElement);
            if (faultElement && faultElement.ExceptionDetails) {
                this.userName = faultElement.ExceptionDetails.UserName;
                this.code = faultElement.ExceptionDetails.Code;
            }
        }
    }
}

export class EOWUserException extends EOWException {
    public readonly resultMessages: IResultMessage[] | undefined;

    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
        if (typeof faultElement === "object" && faultElement.ExceptionDetails) {
            this.resultMessages = convertResultMessages(faultElement.ExceptionDetails.resultMessages) || [];
        }
    }
}

export class EOWRecordPermissionException extends EOWException {

}

export class EOWValidateException extends EOWException {
    public readonly validationErrors: IValidationError[] | undefined;

    constructor(exMessage: string | JSONSRVException, attributeNames?: string[]) {
        if (attributeNames) {
            super(exMessage);
            this.validationErrors = [
                {
                    type: ValidationErrorType.dataError,
                    errorMessage: exMessage as string,
                    attributeNames: attributeNames,
                    linkedAttributes: false,
                },
            ];
        } else if (typeof exMessage === "string") {
            const tmp = EOWException.getParams(exMessage);
            super(tmp.msg ? tmp.msg : exMessage);
        } else {
            super(exMessage);
            if (exMessage && exMessage.ExceptionDetails) {
                this.validationErrors = [];
                for (const error of exMessage.ExceptionDetails.ValidateExceptions)
                    this.validationErrors.push({
                        type: ValidationErrorType.dataError,
                        errorMessage: error.errorMessage,
                        attributeNames: error.attributeNames || [],
                        linkedAttributes: error.linkedAttributes === true,
                    });
            }
        }
    }

    public override toString(): string {
        if (!this.validationErrors || this.validationErrors.length === 0)
            return "Unknown validation error";

        const messages = [] as string[];
        for (const error of this.validationErrors) {
            let message = error.errorMessage;

            if (error.attributeNames && error.attributeNames.length > 0)
                message += ` (${error.attributeNames.join(", ")})`;

            messages.push(message);
        }

        return messages.join("\n");
    }
}

export class EServiceIsNotInitialized extends EOWException {
    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWSessionTimeOut extends EOWException {
    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWKillSession extends EOWException {
    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EReadOnlyAttribute extends EOWException {
    public readonly attributeName: string | undefined;

    constructor(faultElement: string | JSONSRVException) {
        if (typeof faultElement === "string") {
            const tmp = EOWException.getParams(faultElement);
            super(tmp.msg);
            if (tmp.params) {
                this.attributeName = tmp.params[0];
            }
        } else {
            super(faultElement);
            if (faultElement && faultElement.ExceptionDetails)
                this.attributeName = faultElement.ExceptionDetails.AttributeName;
        }
    }
}

export class EReadOnlyMethod extends EOWException {
    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWNoRightsToForm extends EOWException {
    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWNoFormNameException extends EOWException {
    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWFormDefinitionNotFoundException extends EOWException {
    public FormName: string | undefined;

    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWNoImplicitFormForClassException extends EOWException {
    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWFormDefinitionNotModifiedException extends EOWException {
    public FormName: string | undefined;

    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWUnknownException extends EOWException {
    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWConditionException extends EOWException {
    constructor(public readonly conditionID: string, errorText: string) {
        super(errorText);
    }

    public static override [Symbol.hasInstance](instance: any): boolean {
        return instance != null && contextlessInstanceOf(instance, EOWConditionException);
    }
}

export class EOWRightEvaluationException extends EOWException {
    constructor(public readonly itemName: string, errorText: string, public readonly instanceID?: string, public readonly className?: string) {
        super(errorText);
    }

    public static override [Symbol.hasInstance](instance: any): boolean {
        return instance != null && contextlessInstanceOf(instance, EOWRightEvaluationException);
    }
}

export class EInvalidValueException extends EOWException {
    constructor() {
        super("Invalid value!");
    }

    public static override [Symbol.hasInstance](instance: any): boolean {
        return instance != null && contextlessInstanceOf(instance, EInvalidValueException);
    }
}

export class EOWDmsUnavailable extends EOWException {
    constructor(faultElement?: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EDMSException extends EOWException {
    public locKey: string | undefined;

    constructor(faultElement?: string | JSONSRVException, locKey?: string) {
        super(faultElement);
        this.locKey = locKey ? locKey : undefined;
        this.message = this.message.replace("DMS_ERROR:", "").replace(":DMS_ERROR_END", "").trim(); //očistí text EDMSException od wrapper "DMS_ERROR:" a ":DMS_ERROR_END"
    }
}

export class EOWLocalizableException extends EOWException {
    public locKeyHeader: string | undefined;
    public locKeyBody: string | undefined;

    constructor(locKeyHeader?: string, locKeyBody?: string) {
        super("Localizable exception");
        this.locKeyHeader = locKeyHeader ? locKeyHeader : undefined;
        this.locKeyBody = locKeyBody ? locKeyBody : undefined;
    }
}

/**
 * Výjimka při spuštění metody nad více objekty, pokud žádný z objektů nesplňuje podmínku pro spuštění.
 */
export class EOWRunMethodInsufficientRightsException extends EOWException {
    public readonly objectIdentity: string | undefined;

    constructor(faultElement: string | JSONSRVException) {
        super(faultElement);
    }
}

export class EOWUnauthorizedAccess extends EOWException {
}

// pokud definujeme tridu OWExceptions pred definici vyjimek OWExceptions, tak je staticka promenna registeredExceptions naplnena hodnotami undefined :-(
export class OWExceptions {
    private static _exceptionsWatcher: CaseInsensitiveMap<Set<(exception: EOWException) => void>> = new CaseInsensitiveMap<Set<(exception: EOWException) => void>>();

    /**
     * Umožňuje zaregistrovat sledování událostí v aplikaci
     * @param exceptionType Typ výjimky
     * @param fnc Vyvolaná metoda
     */
    public static registerExceptionWatch(exceptionType: string, fnc: (exception: EOWException) => void): void {
        //logger.log(`[OWExceptionWatcher] Register watching '${exceptionType}'`);
        if (!this._exceptionsWatcher.has(exceptionType)) {
            this._exceptionsWatcher.set(exceptionType, new Set<(exception: EOWException) => void>());
        }

        this._exceptionsWatcher.get(exceptionType)!.add(fnc);
    }
    public static unregisterExceptionWatch(exceptionType: string, fnc: () => void): void {
        if (this._exceptionsWatcher.has(exceptionType)) {
            this._exceptionsWatcher.get(exceptionType)!.delete(fnc);
        }
    }

    private static _fireWatchedException(exceptionType: string, exception: EOWException): void {
        if (this._exceptionsWatcher.has(exceptionType)) {
            // událost odešleme v dalším ticku, aby se provedli akce na událost navázané jinde
            self.setTimeout(() => {
                const ev = this._exceptionsWatcher.get(exceptionType);
                if (ev) ev.forEach((v) => v(exception));
            }, 0);
        }
    }

    private static _parseJSON(json: JSONSRVException): { type: string; exception: EOWException } {
        let exT: string = json.ExceptionType;
        const pos = exT.lastIndexOf(".");
        if (pos > 0)
            exT = exT.substring(pos + 1);
        const type = OWExceptions.getRegisteredExceptionType(exT);
        if (type)
            return { type: exT, exception: new type(json) };

        return { type: "EOWException", exception: new EOWException(json) };
    }

    public static parseJSON(json: JSONSRVException): EOWException {
        return this._parseJSON(json).exception;
    }

    public static parseServiceWorkerResponse(data: { statusCode: number; json: JSONSRVException }): EOWException {
        const exc = this._parseJSON(data.json);
        exc.exception.httpErrorCode = data.statusCode;

        this._fireWatchedException(exc.type, exc.exception);

        return exc.exception;
    }

    public static parse(errorResponse: IHTTPResult | XMLHttpRequest, httpRequest?: IHttpExceptionRequest): EOWException {
        if (errorResponse.status != 500 || typeof errorResponse.response !== "string") {
            const exc = new EOWException(errorResponse.status + " - " + errorResponse.statusText);
            exc.httpErrorCode = errorResponse.status;
            exc.request = httpRequest;
            this._fireWatchedException("EOWException", exc);
            return exc;
        } else {
            const contentType = errorResponse instanceof XMLHttpRequest ? errorResponse.getResponseHeader("Content-Type") : errorResponse.contentType;

            if (contentType && contentType.indexOf("application/json") > -1) {
                const exc = OWExceptions._parseJSON(JSON.parse(errorResponse.response));
                exc.exception.httpErrorCode = errorResponse.status;
                exc.exception.request = httpRequest;

                this._fireWatchedException(exc.type, exc.exception);

                return exc.exception;
            }

            let errorText = errorResponse.status + " - " + errorResponse.statusText;
            if (contentType && contentType.indexOf("text/plain") > -1)
                errorText += "\r\n" + errorResponse.response;

            if (contentType && contentType.indexOf("html")) { // pro html i xhtml
                try {
                    const findTitle = /<title>((?:.|\n|\r)*)<\/title>/gi;
                    const execArray = findTitle.exec(errorResponse.response);
                    const title = execArray ? execArray[1].trim() : undefined;
                    if (title) {
                        errorText += "\r\n" + title;
                    }
                } catch (ex) {
                    // Prázdný catch
                }
            }
            const exc = new EOWException(errorText);
            exc.request = httpRequest;
            this._fireWatchedException("EOWException", exc);
            return exc;
        }
    }

    /**
     * Vrátí třídu nalezené výjimky, která byla zaregistrovaná pro deserializaci při příchodu ze služby
     * @param exceptionName název hledané výjimky
     */
    public static getRegisteredExceptionType(exceptionName: string): new (faultElement: any) => EOWException {
        if (!exceptionName)
            throw new InvalidArgumentException("exceptionName");

        return OWExceptions._registeredExceptions[exceptionName];
    }

    private static readonly _registeredExceptions: { [key: string]: new (faultElement: any) => EOWException } = {
        "EOWException": EOWException,
        "EOWLogonError": EOWLogonError,
        "EOWUserException": EOWUserException,
        "EOWValidateException": EOWValidateException,
        "EServiceIsNotInitialized": EServiceIsNotInitialized,
        "EReadOnlyAttribute": EReadOnlyAttribute,
        "EReadOnlyMethod": EReadOnlyMethod,
        "EOWSessionTimeOut": EOWSessionTimeOut,
        "EOWKillSession": EOWKillSession,
        "EOWNoRightsToForm": EOWNoRightsToForm,
        "EOWNoFormNameException": EOWNoFormNameException,
        "EOWFormDefinitionNotFoundException": EOWFormDefinitionNotFoundException,
        "EOWNoImplicitFormForClassException": EOWNoImplicitFormForClassException,
        "EOWUnknownException": EOWUnknownException,
        "EOWRecordPermissionException": EOWRecordPermissionException,
        "EOWFormDefinitionNotModifiedException": EOWFormDefinitionNotModifiedException,
        "EOWDmsUnavailable": EOWDmsUnavailable,
        "EDMSException": EDMSException,
        "EOWLocalizableException": EOWLocalizableException,
        "EOWRunMethodInsufficientRightsException": EOWRunMethodInsufficientRightsException,
        "EOWUnauthorizedAccess": EOWUnauthorizedAccess,
    };
}

/** Výjimka pro špatné přetypování atributu */
export class InvalidTypeCasting extends Error {
    public readonly attributeName: string;
    public readonly requiredClassName: string;
    public readonly className: string;

    /**
     * Výjimka pro špatné přetypování atributu
     * @param attrName Název dotčeného atributu
     * @param requiredClassName Třída, která byla u atributu jako požadovaná
     * @param className Třída, která u objektu byla skutečně
     */
    constructor(attrName: string, requiredClassName: string, className: string) {
        super(`Invalid typecasting on attribute '${attrName}'. Got ${className}, expected ${requiredClassName}.`);

        this.attributeName = attrName;
        this.requiredClassName = requiredClassName;
        this.className = className;
    }
}
